<template>
  <div ref="rootEl" class="relative w-full max-w-xl" data-test="home-global-search">
    <div class="relative">
      <span
        class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-theme-text-muted"
      >
        <Icon :path="mdiMagnify" :size="18" />
      </span>
      <input
        ref="inputEl"
        v-model="query"
        type="text"
        :placeholder="placeholder"
        class="h-10 w-full rounded-theme-md border border-theme-border bg-theme-surface pl-9 pr-14 text-sm text-theme-text placeholder:text-theme-text-muted focus:border-theme-palette-emerald focus:outline-none focus:ring-2 focus:ring-theme-palette-emerald/20 dark:border-theme-border dark:bg-theme-surface-raised dark:placeholder:text-theme-text-secondary"
        @focus="openCompactDropdown"
        @blur="onBlur"
        @keydown.escape="closeAndBlur"
        @keydown.down.prevent="moveFocus(1)"
        @keydown.up.prevent="moveFocus(-1)"
        @keydown.enter.prevent="openResultsDialog"
      />
      <kbd
        v-if="!query"
        class="pointer-events-none absolute inset-y-0 right-2 my-auto inline-flex h-5 items-center rounded border border-theme-border bg-theme-bg-subtle px-1.5 text-[10px] font-medium text-theme-text-secondary dark:border-theme-border dark:bg-theme-surface-elevated"
      >
        {{ shortcutLabel }}
      </kbd>
      <button
        v-else
        type="button"
        class="absolute inset-y-0 right-2 my-auto flex h-7 w-7 items-center justify-center rounded-md text-theme-text-muted transition-colors hover:bg-theme-bg-subtle hover:text-theme-text-secondary focus:outline-none focus:ring-2 focus:ring-theme-palette-emerald/30 dark:hover:bg-theme-surface-elevated dark:hover:text-theme-text-muted"
        :aria-label="t('actions.clear')"
        @mousedown.prevent
        @click="clearQuery"
      >
        <Icon :path="mdiClose" :size="16" />
      </button>
    </div>

    <Teleport to="body">
      <div
        v-if="shouldShowCompactDropdown"
        class="fixed overflow-hidden rounded-theme-lg border border-theme-border bg-theme-surface shadow-theme-dialog dark:border-theme-border dark:bg-theme-surface-raised"
        :style="compactDropdownStyle"
        data-test="home-global-search-results"
        @mousedown.prevent
      >
        <ul class="max-h-96 overflow-y-auto py-1">
          <li
            v-if="isCompactLoading"
            class="flex items-center gap-2 px-3 py-3 text-sm text-theme-text-secondary"
          >
            <span
              class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-theme-border border-t-theme-brand dark:border-theme-border-strong dark:border-t-theme-brand"
              aria-hidden="true"
            ></span>
            {{ placeholder }}
          </li>

          <li v-for="(item, index) in compactResults" :key="`${item.type}:${item.id}`">
            <a
              v-if="item.external"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="flex min-w-0 items-center gap-2 px-3 py-2 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-elevated"
              :class="{ 'bg-theme-bg-subtle dark:bg-theme-surface-elevated': focusedIndex === index }"
              @click="closeAndBlur"
            >
              <Icon :path="iconForType(item.type)" :size="16" class="shrink-0 text-theme-text-muted" />
              <span class="min-w-0 flex-1">
                <span class="block truncate font-medium">{{ item.title }}</span>
                <span
                  v-if="displaySubtitle(item)"
                  class="block truncate text-xs text-theme-text-secondary"
                >
                  {{ displaySubtitle(item) }}
                </span>
              </span>
            </a>
            <RouterLink
              v-else
              :to="item.href"
              class="flex min-w-0 items-center gap-2 px-3 py-2 text-sm text-theme-text-secondary hover:bg-theme-bg-subtle dark:hover:bg-theme-surface-elevated"
              :class="{ 'bg-theme-bg-subtle dark:bg-theme-surface-elevated': focusedIndex === index }"
              @click="closeAndBlur"
            >
              <Icon :path="iconForType(item.type)" :size="16" class="shrink-0 text-theme-text-muted" />
              <span class="min-w-0 flex-1">
                <span class="block truncate font-medium">{{ item.title }}</span>
                <span
                  v-if="displaySubtitle(item)"
                  class="block truncate text-xs text-theme-text-secondary"
                >
                  {{ displaySubtitle(item) }}
                </span>
              </span>
            </RouterLink>
          </li>

          <li
            v-if="!isCompactLoading && !compactResults.length"
            class="px-3 py-3 text-sm text-theme-text-secondary"
          >
            {{ noResultsLabel }}
          </li>
        </ul>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="dialogOpen"
        class="fixed inset-0 z-drawer bg-theme-bg/40 backdrop-blur-sm"
        @click="closeDialog"
      ></div>
      <div
        v-if="dialogOpen"
        class="fixed inset-0 z-dialog flex items-start justify-center px-4 py-8 sm:py-12"
        @click.self="closeDialog"
      >
        <section
          class="flex max-h-[82vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-theme-border bg-theme-surface shadow-2xl dark:border-theme-border dark:bg-theme-surface-raised"
          role="dialog"
          aria-modal="true"
          @keydown.escape="closeDialog"
        >
          <header
            class="flex items-center justify-between gap-3 border-b border-theme-border px-4 py-3 dark:border-theme-border"
          >
            <div class="min-w-0">
              <p class="text-xs font-medium uppercase text-theme-palette-emerald dark:text-theme-palette-emerald">
                {{ searchInLabel }}
              </p>
              <h2 class="truncate text-base font-semibold text-theme-text">
                {{ query.trim() }}
              </h2>
            </div>
            <button
              type="button"
              class="rounded-lg border border-theme-border px-3 py-1.5 text-sm font-medium text-theme-text-secondary hover:bg-theme-bg-subtle dark:border-theme-border dark:hover:bg-theme-surface-elevated"
              @click="closeDialog"
            >
              {{ closeLabel }}
            </button>
          </header>

          <div class="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4">
            <div
              v-if="isDialogLoading"
              class="flex items-center gap-2 px-2 py-8 text-sm text-theme-text-secondary"
            >
              <span
                class="h-4 w-4 animate-spin rounded-full border-2 border-theme-border border-t-theme-brand dark:border-theme-border-strong dark:border-t-theme-brand"
                aria-hidden="true"
              ></span>
              {{ placeholder }}
            </div>

            <div v-else-if="dialogGroups.length" class="space-y-4">
              <section v-for="group in dialogGroups" :key="group.type" class="min-w-0">
                <div class="mb-1.5 flex items-center justify-between gap-2 px-1">
                  <h3 class="text-xs font-semibold uppercase text-theme-text-secondary">
                    {{ labelForType(group.type) }}
                  </h3>
                  <span class="text-xs text-theme-text-muted">
                    {{ group.items.length }}
                  </span>
                </div>

                <div
                  class="overflow-hidden rounded-lg border border-theme-border dark:border-theme-border"
                >
                  <template v-for="item in group.items" :key="`${item.type}:${item.id}`">
                    <a
                      v-if="item.external"
                      :href="item.href"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="flex min-w-0 items-center gap-3 border-b border-theme-border px-3 py-2.5 text-sm last:border-b-0 hover:bg-theme-bg-subtle dark:border-theme-border dark:hover:bg-theme-surface-elevated"
                      @click="closeDialog"
                    >
                      <Icon
                        :path="iconForType(item.type)"
                        :size="17"
                        class="shrink-0 text-theme-text-muted"
                      />
                      <span class="min-w-0 flex-1">
                        <span class="block truncate font-medium text-theme-text">
                          {{ item.title }}
                        </span>
                        <span
                          v-if="displaySubtitle(item)"
                          class="block truncate text-xs text-theme-text-secondary"
                        >
                          {{ displaySubtitle(item) }}
                        </span>
                      </span>
                    </a>
                    <RouterLink
                      v-else
                      :to="item.href"
                      class="flex min-w-0 items-center gap-3 border-b border-theme-border px-3 py-2.5 text-sm last:border-b-0 hover:bg-theme-bg-subtle dark:border-theme-border dark:hover:bg-theme-surface-elevated"
                      @click="closeDialog"
                    >
                      <Icon
                        :path="iconForType(item.type)"
                        :size="17"
                        class="shrink-0 text-theme-text-muted"
                      />
                      <span class="min-w-0 flex-1">
                        <span class="block truncate font-medium text-theme-text">
                          {{ item.title }}
                        </span>
                        <span
                          v-if="displaySubtitle(item)"
                          class="block truncate text-xs text-theme-text-secondary"
                        >
                          {{ displaySubtitle(item) }}
                        </span>
                      </span>
                    </RouterLink>
                  </template>
                </div>
              </section>
            </div>

            <p v-else class="px-2 py-8 text-sm text-theme-text-secondary">
              {{ noResultsLabel }}
            </p>
          </div>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
  import Icon from '@/components/Icon.vue';
import { platformSearchTypes, searchPlatform } from '@/pages/home/composables/platformSearch';
import {
  mdiBattery,
  mdiBell,
  mdiChartBoxOutline,
  mdiChartLine,
  mdiClockOutline,
  mdiClose,
  mdiDatabaseSearchOutline,
  mdiMagnify,
  mdiNewspaperVariantOutline,
  mdiToolboxOutline,
  mdiViewDashboardOutline,
} from '@mdi/js';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

  const { t } = useI18n();

  const placeholder = computed(() => t('home.search_placeholder'));
  const searchInLabel = computed(() => t('home.search_in'));
  const closeLabel = computed(() => t('actions.close'));
  const noResultsLabel = computed(() => t('schema_explainer.no_results'));

  const COMPACT_LIMIT = 7;
  const COMPACT_PER_TYPE_LIMIT = 2;
  const DIALOG_LIMIT = 64;
  const DIALOG_PER_TYPE_LIMIT = 8;
  const MIN_QUERY_LENGTH = 2;
  const SEARCH_DEBOUNCE_MS = 180;

  const rootEl = ref(null);
  const inputEl = ref(null);
  const query = ref('');
  const isOpen = ref(false);
  const focusedIndex = ref(0);
  const compactResults = ref([]);
  const dialogResults = ref([]);
  const isCompactLoading = ref(false);
  const isDialogLoading = ref(false);
  const dialogOpen = ref(false);
  let debounceTimer = null;
  let compactRequestId = 0;
  let positionUpdatePending = false;

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const shortcutLabel = computed(() => (isMac ? t('home.shortcut_mac') : t('home.shortcut_other')));
  const trimmedQuery = computed(() => query.value.trim());
  const shouldShowCompactDropdown = computed(() => Boolean(isOpen.value && trimmedQuery.value));
  const compactDropdownPosition = ref({
    top: 0,
    left: 0,
    width: 0,
  });
  const compactDropdownStyle = computed(() => ({
    position: 'fixed',
    top: `${compactDropdownPosition.value.top}px`,
    left: `${compactDropdownPosition.value.left}px`,
    width: `${compactDropdownPosition.value.width}px`,
    maxWidth: 'calc(100vw - 1rem)',
    zIndex: 140,
  }));

  const typeOrder = platformSearchTypes();

  const dialogGroups = computed(() => {
    const buckets = new Map();
    for (const item of dialogResults.value) {
      if (!buckets.has(item.type)) buckets.set(item.type, []);
      buckets.get(item.type).push(item);
    }
    return typeOrder
      .filter((type) => buckets.has(type))
      .map((type) => ({
        type,
        items: buckets.get(type),
      }));
  });

  const labelsByType = computed(() => ({
    custom_dashboard: t('home.dashboards'),
    custom_kpi: t('home.custom_kpis.title'),
    visualization: t('visualization_marketplace.title'),
    dataset: t('home.platform.datasets'),
    tool: t('menu_tools'),
    tool_run: `${t('menu_tools')} ${t('home.recent')}`,
    active_alert: t('menu_alerts'),
    shadow_trading_asset: t('tools.shadow_trading.sidebar.title'),
    news_article: t('menu_news'),
  }));

  const iconsByType = {
    custom_dashboard: mdiViewDashboardOutline,
    custom_kpi: mdiChartBoxOutline,
    visualization: mdiChartLine,
    dataset: mdiDatabaseSearchOutline,
    tool: mdiToolboxOutline,
    tool_run: mdiClockOutline,
    active_alert: mdiBell,
    shadow_trading_asset: mdiBattery,
    news_article: mdiNewspaperVariantOutline,
  };

  function labelForType(type) {
    return labelsByType.value[type] || type;
  }

  function iconForType(type) {
    return iconsByType[type] || mdiMagnify;
  }

  function displaySubtitle(item) {
    return item?.subtitle || item?.description || '';
  }

  function updateCompactDropdownPosition() {
    if (!rootEl.value || typeof window === 'undefined') return;

    const viewportPadding = 8;
    const rect = rootEl.value.getBoundingClientRect();
    const availableWidth = Math.max(window.innerWidth - viewportPadding * 2, 0);
    const width = Math.min(rect.width, availableWidth);
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      Math.max(window.innerWidth - width - viewportPadding, viewportPadding)
    );

    compactDropdownPosition.value = {
      top: rect.bottom + viewportPadding,
      left,
      width,
    };
  }

  function scheduleCompactDropdownPositionUpdate() {
    if (positionUpdatePending) return;
    positionUpdatePending = true;
    nextTick(() => {
      positionUpdatePending = false;
      updateCompactDropdownPosition();
    });
  }

  function openCompactDropdown() {
    isOpen.value = true;
    updateCompactDropdownPosition();
    scheduleCompactDropdownPositionUpdate();
  }

  async function runSearch({ limit, perTypeLimit }) {
    return searchPlatform({
      q: trimmedQuery.value,
      limit,
      perTypeLimit,
    });
  }

  async function fetchCompactResults() {
    const currentRequestId = ++compactRequestId;
    if (trimmedQuery.value.length < MIN_QUERY_LENGTH) {
      compactResults.value = [];
      isCompactLoading.value = false;
      return;
    }

    isCompactLoading.value = true;
    try {
      const response = await runSearch({
        limit: COMPACT_LIMIT,
        perTypeLimit: COMPACT_PER_TYPE_LIMIT,
      });
      if (currentRequestId !== compactRequestId) return;
      compactResults.value = response.results || [];
      focusedIndex.value = 0;
    } catch (error) {
      if (currentRequestId === compactRequestId) {
        compactResults.value = [];
      }
    } finally {
      if (currentRequestId === compactRequestId) {
        isCompactLoading.value = false;
      }
    }
  }

  function scheduleCompactSearch() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(fetchCompactResults, SEARCH_DEBOUNCE_MS);
  }

  function moveFocus(delta) {
    if (!compactResults.value.length) return;
    focusedIndex.value =
      (focusedIndex.value + delta + compactResults.value.length) % compactResults.value.length;
  }

  async function openResultsDialog() {
    if (trimmedQuery.value.length < MIN_QUERY_LENGTH) return;
    dialogOpen.value = true;
    isOpen.value = false;
    isDialogLoading.value = true;
    try {
      const response = await runSearch({
        limit: DIALOG_LIMIT,
        perTypeLimit: DIALOG_PER_TYPE_LIMIT,
      });
      dialogResults.value = response.results || [];
    } catch (error) {
      dialogResults.value = [];
    } finally {
      isDialogLoading.value = false;
    }
  }

  function closeDialog() {
    dialogOpen.value = false;
    isOpen.value = false;
  }

  function onBlur() {
    setTimeout(() => {
      isOpen.value = false;
    }, 120);
  }

  function closeAndBlur() {
    isOpen.value = false;
    dialogOpen.value = false;
    inputEl.value?.blur();
  }

  function clearQuery() {
    query.value = '';
    nextTick(() => inputEl.value?.focus());
  }

  function onShortcut(event) {
    const meta = event.metaKey || event.ctrlKey;
    if (meta && (event.key === 'k' || event.key === 'K')) {
      event.preventDefault();
      inputEl.value?.focus();
      openCompactDropdown();
    }
  }

  watch(trimmedQuery, () => {
    compactResults.value = [];
    if (!trimmedQuery.value) {
      isCompactLoading.value = false;
      return;
    }
    openCompactDropdown();
    scheduleCompactSearch();
  });

  onMounted(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', onShortcut);
      window.addEventListener('resize', updateCompactDropdownPosition);
      window.addEventListener('scroll', updateCompactDropdownPosition, true);
    }
  });

  onBeforeUnmount(() => {
    window.clearTimeout(debounceTimer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('keydown', onShortcut);
      window.removeEventListener('resize', updateCompactDropdownPosition);
      window.removeEventListener('scroll', updateCompactDropdownPosition, true);
    }
  });
</script>
